import { useState } from 'react';
import { useApp } from '../App';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const CONTRACT_TYPES = [
  {
    id: 'umowa_dzielo',
    name: 'Umowa o dzieło',
    icon: '📝',
    desc: 'Z przeniesieniem praw autorskich (50% KUP)',
    color: '#10b981',
    documents: ['contract', 'rachunek', 'protocol', 'oswiadczenie'],
  },
  {
    id: 'umowa_zlecenie',
    name: 'Umowa zlecenie',
    icon: '📋',
    desc: 'Umowa cywilnoprawna na usługi',
    color: '#6366f1',
    documents: ['contract', 'rachunek'],
  },
  {
    id: 'b2b',
    name: 'Umowa B2B',
    icon: '🏢',
    desc: 'Współpraca z firmą (JDG/Sp. z o.o.)',
    color: '#f59e0b',
    documents: ['contract'],
  },
];

export default function ContractTemplates() {
  const { user, setPage } = useApp();
  const { t } = useI18n();
  const [selectedType, setSelectedType] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  function selectType(type) {
    setSelectedType(type);
  }

  function startQuestionnaire() {
    setShowQuestionnaire(true);
  }

  if (showQuestionnaire && selectedType) {
    return <ContractQuestionnaire type={selectedType} onBack={() => setShowQuestionnaire(false)} setPage={setPage} />;
  }

  return (
    <div className="page">
      <BackButton />
      <h1>📑 Szablony umów</h1>

      {/* Info */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📋 Генератор договоров</div>
        <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>
          Выберите тип договора, заполните анкету — система сгенерирует все необходимые документы.
        </p>
      </div>

      {/* Contract Types */}
      {CONTRACT_TYPES.map(type => (
        <div
          key={type.id}
          onClick={() => selectType(type)}
          style={{
            background: selectedType?.id === type.id 
              ? `${type.color}22` 
              : 'rgba(255,255,255,0.05)',
            border: selectedType?.id === type.id 
              ? `2px solid ${type.color}` 
              : '2px solid transparent',
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32 }}>{type.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold' }}>{type.name}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>{type.desc}</div>
            </div>
            {selectedType?.id === type.id && (
              <span style={{ color: type.color, fontSize: 20 }}>✓</span>
            )}
          </div>

          {selectedType?.id === type.id && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>Документы в комплекте:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {type.documents.map(doc => (
                  <span key={doc} style={{
                    background: 'rgba(255,255,255,0.1)',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                  }}>
                    {doc === 'contract' && '📝 Umowa'}
                    {doc === 'rachunek' && '🧾 Rachunek'}
                    {doc === 'protocol' && '✅ Protokół'}
                    {doc === 'oswiadczenie' && '📋 Oświadczenie'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Start Button */}
      {selectedType && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', marginTop: 16 }}
          onClick={startQuestionnaire}
        >
          📝 Заполнить анкету для {selectedType.name}
        </button>
      )}

      {/* Info about 50% KUP */}
      <div style={{
        background: 'rgba(16,185,129,0.1)',
        borderRadius: 12,
        padding: 16,
        marginTop: 24,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>💡 50% KUP (koszty uzyskania przychodu)</div>
        <div style={{ fontSize: 13, opacity: 0.8 }}>
          <p style={{ margin: '8px 0' }}>
            Umowa o dzieło z przeniesieniem praw autorskich позволяет применять 50% KUP, 
            что значительно снижает налоговую нагрузку.
          </p>
          <p style={{ margin: '8px 0' }}>
            <strong>Требования:</strong>
          </p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Результат работы — авторское произведение</li>
            <li>Передача авторских прав заказчику</li>
            <li>Детальное описание выполненной работы</li>
            <li>Протокол приёма-передачи</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// Questionnaire Component
function ContractQuestionnaire({ type, onBack, setPage }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    // Wykonawca (Contractor)
    wykonawca_name: '',
    wykonawca_surname: '',
    wykonawca_pesel: '',
    wykonawca_id_type: 'dowod', // dowod, passport
    wykonawca_id_number: '',
    wykonawca_address: '',
    wykonawca_city: '',
    wykonawca_postal: '',
    wykonawca_phone: '',
    wykonawca_email: '',
    wykonawca_bank_name: '',
    wykonawca_bank_account: '',
    wykonawca_tax_office: '',
    
    // Work details
    work_description: '',
    work_type: 'it', // it, design, copywriting, translation, other
    work_result: '',
    work_start_date: new Date().toISOString().split('T')[0],
    work_end_date: '',
    
    // Payment
    payment_amount: '',
    payment_currency: 'PLN',
    payment_type: 'jednorazowo', // jednorazowo, miesiecznie
    
    // Tax info
    has_other_employment: false,
    wants_pit2: false,
    tax_deduction_type: '50', // 50, 20, 0
    
    // Health insurance
    has_zus: false,
    student_under_26: false,
  });
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);

  const totalSteps = type.id === 'umowa_dzielo' ? 4 : 3;

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function nextStep() {
    if (step < totalSteps) setStep(step + 1);
    else generateDocuments();
  }

  function prevStep() {
    if (step > 1) setStep(step - 1);
    else onBack();
  }

  async function generateDocuments() {
    setGenerating(true);
    
    // Simulate generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const docs = generateContractDocuments(type, form);
    setGenerated(docs);
    setGenerating(false);
  }

  if (generating) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 100 }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>⚙️</div>
        <h2>Генерация документов...</h2>
        <p style={{ opacity: 0.7 }}>Создаём {type.name} и сопутствующие документы</p>
      </div>
    );
  }

  if (generated) {
    return (
      <div className="page">
        <BackButton onClick={onBack} />
        <h1>✅ Документы готовы!</h1>

        <div style={{
          background: 'rgba(16,185,129,0.1)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
            {type.icon} {type.name}
          </div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            Wykonawca: {form.wykonawca_name} {form.wykonawca_surname}<br />
            Kwota: {form.payment_amount} {form.payment_currency}
          </div>
        </div>

        {/* Document List */}
        {generated.documents.map((doc, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{doc.icon} {doc.name}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{doc.desc}</div>
              </div>
              <button
                className="btn btn-small"
                style={{ background: 'rgba(99,102,241,0.3)' }}
                onClick={() => downloadDocument(doc)}
              >
                📥 PDF
              </button>
            </div>
          </div>
        ))}

        {/* Preview */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 16,
          marginTop: 16,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📄 Предпросмотр умовы:</div>
          <pre style={{
            background: 'rgba(0,0,0,0.3)',
            padding: 12,
            borderRadius: 8,
            fontSize: 10,
            whiteSpace: 'pre-wrap',
            maxHeight: 300,
            overflow: 'auto',
          }}>
            {generated.preview}
          </pre>
        </div>

        <button
          className="btn btn-primary"
          style={{ width: '100%', marginTop: 16 }}
          onClick={() => {
            // Save to contracts
            localStorage.setItem('last_contract', JSON.stringify({ type, form, generated }));
            setPage('my_contracts');
          }}
        >
          💾 Сохранить в "Мои договора"
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <BackButton onClick={prevStep} />
      <h1>{type.icon} {type.name}</h1>

      {/* Progress */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} style={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            background: i + 1 <= step ? '#10b981' : 'rgba(255,255,255,0.2)',
          }} />
        ))}
      </div>

      {/* Step 1: Personal Data */}
      {step === 1 && (
        <div className="form">
          <h3>👤 Dane wykonawcy (Исполнитель)</h3>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>Imię *</label>
              <input
                value={form.wykonawca_name}
                onChange={e => updateField('wykonawca_name', e.target.value)}
                placeholder="Jan"
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Nazwisko *</label>
              <input
                value={form.wykonawca_surname}
                onChange={e => updateField('wykonawca_surname', e.target.value)}
                placeholder="Kowalski"
              />
            </div>
          </div>

          <div className="field">
            <label>PESEL *</label>
            <input
              value={form.wykonawca_pesel}
              onChange={e => updateField('wykonawca_pesel', e.target.value)}
              placeholder="00000000000"
              maxLength={11}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ width: 120 }}>
              <label>Dokument</label>
              <select
                value={form.wykonawca_id_type}
                onChange={e => updateField('wykonawca_id_type', e.target.value)}
              >
                <option value="dowod">Dowód</option>
                <option value="passport">Paszport</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Numer *</label>
              <input
                value={form.wykonawca_id_number}
                onChange={e => updateField('wykonawca_id_number', e.target.value)}
                placeholder="ABC123456"
              />
            </div>
          </div>

          <div className="field">
            <label>Adres zamieszkania *</label>
            <input
              value={form.wykonawca_address}
              onChange={e => updateField('wykonawca_address', e.target.value)}
              placeholder="ul. Przykładowa 1/2"
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ width: 100 }}>
              <label>Kod pocztowy</label>
              <input
                value={form.wykonawca_postal}
                onChange={e => updateField('wykonawca_postal', e.target.value)}
                placeholder="00-000"
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Miasto *</label>
              <input
                value={form.wykonawca_city}
                onChange={e => updateField('wykonawca_city', e.target.value)}
                placeholder="Warszawa"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>Telefon</label>
              <input
                value={form.wykonawca_phone}
                onChange={e => updateField('wykonawca_phone', e.target.value)}
                placeholder="+48 000 000 000"
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t('common.email')}</label>
              <input
                value={form.wykonawca_email}
                onChange={e => updateField('wykonawca_email', e.target.value)}
                placeholder="email@example.com"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Bank & Tax */}
      {step === 2 && (
        <div className="form">
          <h3>🏦 Dane bankowe i podatkowe</h3>
          
          <div className="field">
            <label>Nazwa banku</label>
            <input
              value={form.wykonawca_bank_name}
              onChange={e => updateField('wykonawca_bank_name', e.target.value)}
              placeholder="PKO BP, mBank, etc."
            />
          </div>

          <div className="field">
            <label>Numer konta (IBAN) *</label>
            <input
              value={form.wykonawca_bank_account}
              onChange={e => updateField('wykonawca_bank_account', e.target.value)}
              placeholder="PL00 0000 0000 0000 0000 0000 0000"
            />
          </div>

          <div className="field">
            <label>Urząd Skarbowy</label>
            <input
              value={form.wykonawca_tax_office}
              onChange={e => updateField('wykonawca_tax_office', e.target.value)}
              placeholder="Pierwszy Urząd Skarbowy w Warszawie"
            />
          </div>

          <h3 style={{ marginTop: 24 }}>📋 Oświadczenia</h3>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(255,255,255,0.05)', borderRadius: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={form.has_other_employment}
              onChange={e => updateField('has_other_employment', e.target.checked)}
            />
            <span>Mam inne zatrudnienie (umowa o pracę/zlecenie)</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(255,255,255,0.05)', borderRadius: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={form.student_under_26}
              onChange={e => updateField('student_under_26', e.target.checked)}
            />
            <span>Student/uczeń do 26 roku życia</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(255,255,255,0.05)', borderRadius: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={form.wants_pit2}
              onChange={e => updateField('wants_pit2', e.target.checked)}
            />
            <span>Chcę złożyć PIT-2 (ulga podatkowa)</span>
          </label>

          {type.id === 'umowa_dzielo' && (
            <div className="field" style={{ marginTop: 16 }}>
              <label>Koszty uzyskania przychodu</label>
              <select
                value={form.tax_deduction_type}
                onChange={e => updateField('tax_deduction_type', e.target.value)}
              >
                <option value="50">50% KUP (prawa autorskie)</option>
                <option value="20">20% KUP (standardowe)</option>
                <option value="0">Bez KUP</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Work Details */}
      {step === 3 && (
        <div className="form">
          <h3>💼 Szczegóły pracy</h3>

          <div className="field">
            <label>Rodzaj pracy</label>
            <select
              value={form.work_type}
              onChange={e => updateField('work_type', e.target.value)}
            >
              <option value="it">💻 IT / Programowanie</option>
              <option value="design">🎨 Grafika / Design</option>
              <option value="copywriting">✍️ Copywriting / Teksty</option>
              <option value="translation">🌍 Tłumaczenia</option>
              <option value="marketing">📱 Marketing</option>
              <option value="consulting">💼 Konsulting</option>
              <option value="other">📋 Inne</option>
            </select>
          </div>

          <div className="field">
            <label>Opis dzieła / Przedmiot umowy *</label>
            <textarea
              value={form.work_description}
              onChange={e => updateField('work_description', e.target.value)}
              placeholder="Szczegółowy opis pracy do wykonania, np.: Stworzenie strony internetowej w technologii React..."
              rows={4}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff', resize: 'vertical' }}
            />
          </div>

          {type.id === 'umowa_dzielo' && (
            <div className="field">
              <label>Rezultat pracy (utwór) *</label>
              <textarea
                value={form.work_result}
                onChange={e => updateField('work_result', e.target.value)}
                placeholder="Co będzie przekazane jako efekt pracy, np.: Kod źródłowy aplikacji, projekt graficzny w formacie .psd/.fig..."
                rows={3}
                style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff', resize: 'vertical' }}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>Data rozpoczęcia</label>
              <input
                type="date"
                value={form.work_start_date}
                onChange={e => updateField('work_start_date', e.target.value)}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Data zakończenia</label>
              <input
                type="date"
                value={form.work_end_date}
                onChange={e => updateField('work_end_date', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>Wynagrodzenie brutto *</label>
              <input
                type="number"
                value={form.payment_amount}
                onChange={e => updateField('payment_amount', e.target.value)}
                placeholder="5000"
              />
            </div>
            <div className="field" style={{ width: 100 }}>
              <label>Waluta</label>
              <select
                value={form.payment_currency}
                onChange={e => updateField('payment_currency', e.target.value)}
              >
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label>Sposób wypłaty</label>
            <select
              value={form.payment_type}
              onChange={e => updateField('payment_type', e.target.value)}
            >
              <option value="jednorazowo">Jednorazowo (po wykonaniu)</option>
              <option value="miesiecznie">Miesięcznie</option>
              <option value="etapami">Etapami</option>
            </select>
          </div>
        </div>
      )}

      {/* Step 4: Summary (for umowa o dzielo) */}
      {step === 4 && type.id === 'umowa_dzielo' && (
        <div className="form">
          <h3>✅ Podsumowanie</h3>

          <div style={{
            background: 'rgba(16,185,129,0.1)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
          }}>
            <h4 style={{ margin: '0 0 12px' }}>👤 Wykonawca</h4>
            <p style={{ margin: 4, fontSize: 14 }}><strong>{form.wykonawca_name} {form.wykonawca_surname}</strong></p>
            <p style={{ margin: 4, fontSize: 13, opacity: 0.8 }}>PESEL: {form.wykonawca_pesel}</p>
            <p style={{ margin: 4, fontSize: 13, opacity: 0.8 }}>{form.wykonawca_address}, {form.wykonawca_postal} {form.wykonawca_city}</p>
          </div>

          <div style={{
            background: 'rgba(99,102,241,0.1)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
          }}>
            <h4 style={{ margin: '0 0 12px' }}>💼 Przedmiot umowy</h4>
            <p style={{ margin: 4, fontSize: 13 }}>{form.work_description || 'Nie podano'}</p>
            <p style={{ margin: '12px 0 4px', fontSize: 13, opacity: 0.8 }}>
              Okres: {form.work_start_date} — {form.work_end_date || 'nie określono'}
            </p>
          </div>

          <div style={{
            background: 'rgba(245,158,11,0.1)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
          }}>
            <h4 style={{ margin: '0 0 12px' }}>💰 Wynagrodzenie</h4>
            <p style={{ margin: 4, fontSize: 20, fontWeight: 'bold' }}>
              {form.payment_amount} {form.payment_currency} brutto
            </p>
            <p style={{ margin: 4, fontSize: 13, opacity: 0.8 }}>
              KUP: {form.tax_deduction_type}% • Wypłata: {form.payment_type}
            </p>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 12,
            padding: 16,
          }}>
            <h4 style={{ margin: '0 0 12px' }}>📄 Dokumenty do wygenerowania</h4>
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13 }}>
              <li>📝 Umowa o dzieło z przeniesieniem praw autorskich</li>
              <li>🧾 Rachunek do umowy o dzieło</li>
              <li>✅ Protokół zdawczo-odbiorczy dzieła</li>
              <li>📋 Oświadczenie wykonawcy (PIT)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
        <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={prevStep}>
          ← Назад
        </button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={nextStep}>
          {step === totalSteps ? '📄 Сгенерировать документы' : 'Далее →'}
        </button>
      </div>
    </div>
  );
}

// Document generation
function generateContractDocuments(type, form) {
  const today = new Date().toLocaleDateString('pl-PL');
  const companyName = 'WORKIFYPL SP. Z O.O.';
  const companyNip = '0000000000';
  const companyAddress = 'ul. Przykładowa 1, 00-000 Warszawa';

  let preview = '';
  const documents = [];

  if (type.id === 'umowa_dzielo') {
    preview = `UMOWA O DZIEŁO Z PRZENIESIENIEM PRAW AUTORSKICH
Nr: UD/${Date.now().toString().slice(-6)}/${new Date().getFullYear()}

zawarta w dniu ${today} w Warszawie
pomiędzy:

ZAMAWIAJĄCY:
${companyName}
NIP: ${companyNip}
${companyAddress}

a

WYKONAWCA:
${form.wykonawca_name} ${form.wykonawca_surname}
PESEL: ${form.wykonawca_pesel}
${form.wykonawca_id_type === 'dowod' ? 'Dowód osobisty' : 'Paszport'}: ${form.wykonawca_id_number}
Adres: ${form.wykonawca_address}, ${form.wykonawca_postal} ${form.wykonawca_city}

§1. PRZEDMIOT UMOWY
1. Zamawiający zleca, a Wykonawca zobowiązuje się wykonać dzieło polegające na:
   ${form.work_description}

2. Rezultatem pracy będzie:
   ${form.work_result}

§2. TERMIN WYKONANIA
Dzieło zostanie wykonane w terminie od ${form.work_start_date} do ${form.work_end_date || 'uzgodnionego przez strony'}.

§3. WYNAGRODZENIE
1. Za wykonanie dzieła Wykonawca otrzyma wynagrodzenie w wysokości ${form.payment_amount} ${form.payment_currency} brutto.
2. Wynagrodzenie obejmuje przeniesienie praw autorskich.
3. Wypłata: ${form.payment_type === 'jednorazowo' ? 'jednorazowo po odbiorze dzieła' : form.payment_type}.

§4. PRZENIESIENIE PRAW AUTORSKICH
1. Wykonawca przenosi na Zamawiającego całość autorskich praw majątkowych do dzieła.
2. Przeniesienie praw następuje z chwilą odbioru dzieła.
3. Przeniesienie obejmuje wszystkie pola eksploatacji, w szczególności:
   a) utrwalanie i zwielokrotnianie,
   b) obrót oryginałem i egzemplarzami,
   c) rozpowszechnianie w dowolny sposób.

§5. KOSZTY UZYSKANIA PRZYCHODU
Strony zgodnie oświadczają, że przedmiotem umowy jest utwór w rozumieniu prawa autorskiego,
co uprawnia do zastosowania 50% kosztów uzyskania przychodu.

§6. POSTANOWIENIA KOŃCOWE
1. Wszelkie zmiany umowy wymagają formy pisemnej.
2. W sprawach nieuregulowanych stosuje się przepisy Kodeksu cywilnego.
3. Umowa sporządzona w dwóch jednobrzmiących egzemplarzach.


_____________________          _____________________
    ZAMAWIAJĄCY                    WYKONAWCA`;

    documents.push(
      { name: 'Umowa o dzieło', icon: '📝', desc: 'Główny dokument umowy', filename: 'umowa_o_dzielo.pdf' },
      { name: 'Rachunek', icon: '🧾', desc: 'Rachunek do umowy o dzieło', filename: 'rachunek.pdf' },
      { name: 'Protokół zdawczo-odbiorczy', icon: '✅', desc: 'Potwierdzenie odbioru dzieła', filename: 'protokol.pdf' },
      { name: 'Oświadczenie wykonawcy', icon: '📋', desc: 'Oświadczenie dla celów podatkowych', filename: 'oswiadczenie.pdf' },
    );
  }

  return { preview, documents };
}

function downloadDocument(doc) {
  // In real app, generate PDF on backend
  alert(`📥 Pobieranie: ${doc.filename}\n\nW pełnej wersji dokument zostanie wygenerowany jako PDF.`);
}

